/* eslint-disable react-refresh/only-export-components -- vendored shadcn/ui component */
import * as React from 'react';
import * as RechartsPrimitive from 'recharts';

import type { ChartLegendItem } from '../../lib/charts/legend';
import { cn } from '../../lib/utils';

type ChartConfig = Record<
  string,
  {
    label: string;
    color: string;
    icon?: React.ComponentType;
  }
>;

interface ChartContextProps {
  config: ChartConfig;
}

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart(): ChartContextProps {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }
  return context;
}

// Re-exported from shared module so existing imports still work
export { CHART_COLORS } from '../../lib/charts/colors';

/** Fixed height (px) for the cartesian plot region, shared by agent charts
 *  (ChartBlock) and dashboard panels (DashboardPanel). Callers add the x-axis
 *  label height on top so angled labels grow the box, not shrink the plot. */
export const CHART_PLOT_HEIGHT = 300;

// Themed recharts chrome (axis text/line, grid, tooltip cursor, polar/radial, sectors) as token
// classes on the chart wrapper so it flips with the theme. Shared with ChartBlock so the two
// renderers can't drift; recharts sets these colors as presentation attrs, which the CSS overrides.
export const CHART_THEME_SELECTORS =
  '[&_.recharts-cartesian-axis-line]:stroke-border [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke]]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke]]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-border/30 [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-border/20 [&_.recharts-reference-line_[stroke]]:stroke-border [&_.recharts-sector[stroke]]:stroke-background [&_.recharts-surface]:outline-none';

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
    chartHeight?: number;
  }
>(({ id, className, children, config, chartHeight, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, '')}`;

  const style = React.useMemo(() => {
    const vars: Record<string, string> = {};
    for (const [key, value] of Object.entries(config)) {
      vars[`--color-${key}`] = value.color;
    }
    return vars;
  }, [config]);

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        ref={ref}
        data-chart={chartId}
        className={cn(
          'flex justify-center text-xs',
          CHART_THEME_SELECTORS,
          chartHeight == null && 'aspect-video',
          className,
        )}
        style={style}
        {...props}
      >
        <RechartsPrimitive.ResponsiveContainer height={chartHeight}>{children}</RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = 'ChartContainer';

const ChartTooltip = RechartsPrimitive.Tooltip;

interface TooltipPayloadItem {
  name?: string;
  value?: string | number;
  dataKey?: string | number;
  color?: string;
  payload?: Record<string, unknown>;
}

interface ChartTooltipContentProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  hideLabel?: boolean;
  hideIndicator?: boolean;
  indicator?: 'line' | 'dot' | 'dashed';
  nameKey?: string;
  labelKey?: string;
  labelFormatter?: (label: string) => React.ReactNode;
  className?: string;
}

const ChartTooltipContent = React.forwardRef<HTMLDivElement, ChartTooltipContentProps>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelKey,
      labelFormatter,
      nameKey,
    },
    ref,
  ) => {
    const { config } = useChart();

    if (!active || !payload?.length) {
      return null;
    }

    const baseLabel = labelKey ? (config[labelKey]?.label ?? label) : label;
    const tooltipLabel =
      labelFormatter && baseLabel != null && baseLabel.trim() !== '' ? labelFormatter(baseLabel) : baseLabel;

    return (
      <div
        ref={ref}
        className={cn(
          'grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs shadow-xl',
          className,
        )}
      >
        {!hideLabel && tooltipLabel ? <div className="font-medium text-foreground">{tooltipLabel}</div> : null}
        <div className="grid gap-1.5">
          {payload.map((item) => {
            const key = nameKey ? ((item.payload?.[nameKey] as string) ?? item.name) : item.name;
            const itemConfig = config[key ?? ''];
            const indicatorColor = item.color ?? itemConfig?.color ?? 'var(--color-brand)';

            return (
              <div
                key={item.dataKey}
                className="flex items-center gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground"
              >
                {!hideIndicator && (
                  <div
                    className={cn(
                      'shrink-0 rounded-[2px] border-[--color-border]',
                      indicator === 'dot' && 'h-2.5 w-2.5 rounded-full',
                      indicator === 'line' && 'h-0.5 w-6',
                      indicator === 'dashed' && 'h-0.5 w-6 border-b-2 border-dashed bg-transparent',
                    )}
                    style={
                      indicator === 'dashed' ? { borderColor: indicatorColor } : { backgroundColor: indicatorColor }
                    }
                  />
                )}
                <div className="flex flex-1 items-baseline justify-between gap-4 leading-none">
                  <div className="text-muted-foreground">{itemConfig?.label ?? key}</div>
                  <div className="font-mono font-medium text-foreground tabular-nums">
                    {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = 'ChartTooltipContent';

/**
 * Standalone HTML legend rendered OUTSIDE the Recharts chart (as a sibling below
 * it), so it can wrap to multiple rows without colliding with the fixed-height
 * plot. Recharts' built-in <Legend> reserves plot space via a ref-callback
 * measurement (useElementOffset) that is NOT a ResizeObserver — it goes stale
 * when the legend re-wraps after a width change, leaving the extra rows
 * overlapping the chart. Rendering the legend as a sibling lets the container
 * grow instead.
 */
function ChartLegendList({
  items,
  className,
}: {
  items: ChartLegendItem[];
  className?: string;
}): React.ReactElement | null {
  if (items.length === 0) return null;

  return (
    <div className={cn('flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-3 text-xs', className)}>
      {items.map((item) => (
        <div key={item.key} className="flex items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ backgroundColor: item.color }} />
          <span className="text-muted-foreground">{item.label}</span>
        </div>
      ))}
    </div>
  );
}
ChartLegendList.displayName = 'ChartLegendList';

export { ChartContainer, ChartContext, ChartLegendList, ChartTooltip, ChartTooltipContent, useChart };
export type { ChartConfig };
